import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState
} from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProfileAlert } from "Api/billingSystem/profile/getProfileAlert.request";
import { patchProfileAlert } from "Api/billingSystem/profile/patchProfileAlert.request";
import { deleteProfileAlert } from "Api/billingSystem/profile/deleteProfileAlert.request";

import type { RootState } from "Store/configureStore";
import type { ProfileAlert } from "Api/billingSystem/types";

// Thunks
export const getProfileAlertThunk = createAppAsyncThunk(
  "billingSystem/profileAlerts/get",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      const alerts = await getProfileAlert(billingProfileId);
      const billingAlert = alerts.find(a => a.type === "billing") ?? alerts[0];

      if (!billingAlert) {
        return rejectWithValue({ error: "No billing alert found" });
      }

      // Key the entity by a synthetic id derived from billingProfileId so
      // selectors can look the alert up without knowing the backend's real id.
      return {
        ...billingAlert,
        id: `alert-${billingProfileId}`
      };
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const patchProfileAlertThunk = createAppAsyncThunk(
  "billingSystem/profileAlerts/patch",
  async (
    {
      billingProfileId,
      threshold
    }: { billingProfileId: string; threshold: number },
    { rejectWithValue }
  ) => {
    try {
      const alert = await patchProfileAlert(billingProfileId, threshold);
      return {
        ...alert,
        id: `alert-${billingProfileId}`
      };
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const deleteProfileAlertThunk = createAppAsyncThunk(
  "billingSystem/profileAlerts/delete",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      const alert = await deleteProfileAlert(billingProfileId);
      return {
        ...alert,
        id: `alert-${billingProfileId}`
      };
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

const profileAlertsAdapter = createEntityAdapter({
  selectId: (alert: ProfileAlert) => alert.id
});

// State type
type ProfileAlertsState = EntityState<ProfileAlert, string> & {
  alertByProfileIdState: Record<
    string,
    {
      loading: boolean;
      error?: string;
    }
  >;
  patchState: Record<
    string,
    {
      loading: boolean;
      error?: string;
    }
  >;
};

const initialState: ProfileAlertsState = profileAlertsAdapter.getInitialState({
  alertByProfileIdState: {},
  patchState: {}
});

// Slice
const profileAlertsSlice = createSlice({
  name: "profileAlerts",
  initialState,
  reducers: {
    clearPatchError(state, action: { payload: { billingProfileId: string } }) {
      const { billingProfileId } = action.payload;
      setDeep(state, ["patchState", billingProfileId, "error"], undefined);
    }
  },
  extraReducers: builder => {
    // Get profile alert
    builder
      .addCase(getProfileAlertThunk.pending, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(
          state,
          ["alertByProfileIdState", billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["alertByProfileIdState", billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getProfileAlertThunk.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(
          state,
          ["alertByProfileIdState", billingProfileId, "loading"],
          false
        );
        profileAlertsAdapter.setOne(state, action.payload);
      })
      .addCase(getProfileAlertThunk.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(
          state,
          ["alertByProfileIdState", billingProfileId, "loading"],
          false
        );
        setDeep(
          state,
          ["alertByProfileIdState", billingProfileId, "error"],
          action.payload?.error
        );
      });

    // Patch profile alert
    builder
      .addCase(patchProfileAlertThunk.pending, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["patchState", billingProfileId, "loading"], true);
        setDeep(state, ["patchState", billingProfileId, "error"], undefined);
      })
      .addCase(patchProfileAlertThunk.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["patchState", billingProfileId, "loading"], false);
        profileAlertsAdapter.setOne(state, action.payload);
      })
      .addCase(patchProfileAlertThunk.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["patchState", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["patchState", billingProfileId, "error"],
          action.payload?.error
        );
      });

    // Delete profile alert
    builder
      .addCase(deleteProfileAlertThunk.pending, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["patchState", billingProfileId, "loading"], true);
        setDeep(state, ["patchState", billingProfileId, "error"], undefined);
      })
      .addCase(deleteProfileAlertThunk.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["patchState", billingProfileId, "loading"], false);
        profileAlertsAdapter.setOne(state, action.payload);
      })
      .addCase(deleteProfileAlertThunk.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["patchState", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["patchState", billingProfileId, "error"],
          action.payload?.error
        );
      });
  }
});

// Selectors
const selectProfileAlertsState = (state: RootState) => state.profileAlerts;

const profileAlertsSelectors = profileAlertsAdapter.getSelectors(
  selectProfileAlertsState
);

const selectAllProfileAlerts = profileAlertsSelectors.selectAll;

export const selectProfileAlert = createSelector(
  [
    selectAllProfileAlerts,
    (_: RootState, billingProfileId: string) => billingProfileId
  ],
  (alerts, billingProfileId) =>
    alerts.find(alert => alert.id === `alert-${billingProfileId}`)
);

export const selectProfileAlertLoaded = createSelector(
  [
    selectProfileAlertsState,
    (_: RootState, billingProfileId: string) => billingProfileId
  ],
  (state, billingProfileId) => billingProfileId in state.alertByProfileIdState
);

export const selectProfileAlertLoading = createSelector(
  [
    selectProfileAlertsState,
    (_: RootState, billingProfileId: string) => billingProfileId
  ],
  (state, billingProfileId) =>
    state.alertByProfileIdState[billingProfileId]?.loading ?? false
);

export const selectProfileAlertError = createSelector(
  [
    selectProfileAlertsState,
    (_: RootState, billingProfileId: string) => billingProfileId
  ],
  (state, billingProfileId) =>
    state.alertByProfileIdState[billingProfileId]?.error
);

export const selectProfileAlertPatchLoading = createSelector(
  [
    selectProfileAlertsState,
    (_: RootState, billingProfileId: string) => billingProfileId
  ],
  (state, billingProfileId) =>
    state.patchState[billingProfileId]?.loading ?? false
);

export const selectProfileAlertPatchError = createSelector(
  [
    selectProfileAlertsState,
    (_: RootState, billingProfileId: string) => billingProfileId
  ],
  (state, billingProfileId) => state.patchState[billingProfileId]?.error
);

export const { clearPatchError } = profileAlertsSlice.actions;

export default profileAlertsSlice.reducer;
